import {RootLayout, RsnInput, Skeleton} from '@raison/ui-kit';
import {useQuery} from '@tanstack/react-query';
import {Atom, useAtomValue} from 'jotai';
import React, {useMemo} from 'react';
import {IAlert} from '~/entities/alerts';
import {CurrencyInfo, findCryptoCurrencyByCode, useCurrenciesInfoOptions} from '~/entities/currencies';
import {Decoder} from '@raison/prelude';
import {ReceivingTrTxPage, ReceivingTrTxResources} from '../ui/ReceivingTrTxPage';
import {findReceiveTrTxAlert} from '../ui/TrTxAlertHandlers';
import {TransactionSentForReview} from '../ui/views/SendTrTx.views';
import {OrderId} from './TrTx';
import {submittedReceiveTrTxIds$} from './TrTxIdsRegistry';
import {useUserInfoResource} from './useUserInfoResource';

export function ReceivingTrTxPageContainer({
    orderId: maybeOrderId,
    alerts$,
}: {
    orderId: string;
    alerts$: Atom<{data: undefined | IAlert[]; isLoading: boolean}>;
}) {
    const orderId = useMemo(() => Decoder.run(OrderId, Number(maybeOrderId)).orThrow(), [maybeOrderId]);

    const userQuery = useUserInfoResource();
    const {data: alertsList, ...alertsQuery} = useAtomValue(alerts$);
    const {data: currenciesInfo, ...currenciesQuery} = useQuery(useCurrenciesInfoOptions());

    const trtxQuery = {
        isLoading: currenciesQuery.isLoading || alertsQuery.isLoading,
        data: currenciesInfo && alertsList && findAndEnrichReceiveTrTxAlert(currenciesInfo, alertsList, orderId),
    };
    const submittedIdsRegistry = useAtomValue(submittedReceiveTrTxIds$);

    if (userQuery.isLoading || trtxQuery.isLoading) {
        return <SkeletonPage />;
    }

    if (!userQuery.data || !trtxQuery.data) {
        return <SkeletonPage />;
    }

    if (submittedIdsRegistry.has(trtxQuery.data.trtx.id)) {
        return <TransactionSentForReview />;
    }

    return (
        <RootLayout common title='Receive'>
            <ReceivingTrTxPage resources={{...userQuery.data, ...trtxQuery.data}} />
        </RootLayout>
    );
}

function findAndEnrichReceiveTrTxAlert(
    currenciesInfo: CurrencyInfo[],
    alertsList: IAlert<unknown>[],
    orderId: OrderId
): undefined | Pick<ReceivingTrTxResources, 'trtx'> {
    const trtAlert = findReceiveTrTxAlert(orderId)(alertsList);
    const currencyInfo = trtAlert && findCryptoCurrencyByCode(trtAlert.currency)(currenciesInfo);
    return (
        currencyInfo && {
            trtx: {...trtAlert, currency: currencyInfo},
        }
    );
}

function SkeletonPage() {
    return (
        <RootLayout common title='Receive'>
            <div className='grid gap-2'>
                <div className='h-7'>
                    <Skeleton className='h-full' />
                </div>
                <div className='fv-text-l'>
                    <Skeleton />
                    <Skeleton width={180} />
                </div>
            </div>
            <RsnInput.Skeleton />
            <RsnInput.Skeleton />
            <RsnInput.Skeleton />
            <RsnInput.Skeleton />
            <div className='mt-auto'>
                <Skeleton borderRadius={16} width={'100%'} height={48} />
            </div>
        </RootLayout>
    );
}
